import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { dataSheetTokens } from '../DataSheet/tokens';

export type DropdownOption = {
    label: string;
    value: string | number;
};

interface FilterDropdownProps {
    label?: string;
    value: string | number;
    options: DropdownOption[];
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    /** Compact mode - uses smaller height matching DataSheet controls */
    compact?: boolean;
}

const tokens = dataSheetTokens;

const FilterDropdown: React.FC<FilterDropdownProps> = ({
    label,
    value,
    options,
    onChange,
    placeholder = 'Select',
    className,
    compact = true, // Default to compact mode for consistency
}) => {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef<HTMLDivElement | null>(null);

    const selectedOption =
        options.find((opt) => String(opt.value) === String(value)) || null;

    // outside click handle
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (val: string | number) => {
        onChange(String(val));
        setOpen(false);
    };

    return (
        <div
            ref={containerRef}
            className={clsx(
                'relative flex flex-col gap-1',
                className,
            )}
            style={{ minWidth: compact ? 120 : 180 }}
        >
            {label && (
                <span 
                    style={{
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: tokens.colors.textSecondary,
                    }}
                >
                    {label}
                </span>
            )}

            {/* trigger */}
            <button
                type="button"
                onClick={() => {
                    setOpen((prev) => !prev);
                    if (!open) setSearchTerm('');
                }}
                style={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: compact ? tokens.dimensions.controlHeight : 36,
                    padding: `0 ${tokens.spacing.sm}px`,
                    fontSize: 12,
                    fontWeight: 500,
                    color: selectedOption ? tokens.colors.textPrimary : tokens.colors.textMuted,
                    backgroundColor: tokens.colors.surface,
                    border: `1px solid ${open ? 'rgb(1, 149, 255)' : tokens.colors.border}`,
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    boxShadow: open ? '0 0 0 2px rgba(1, 149, 255, 0.1)' : 'none',
                }}
                onMouseEnter={(e) => {
                    if (!open) {
                        (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(0,0,0,0.12)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!open) {
                        (e.currentTarget as HTMLButtonElement).style.borderColor = tokens.colors.border;
                    }
                }}
            >
                <span style={{ 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                }}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>

                <span
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: tokens.spacing.xs,
                        color: tokens.colors.textMuted,
                        transition: 'transform 0.15s ease',
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                >
                    <svg width="10" height="10" viewBox="0 0 20 20" fill="none">
                        <path
                            d="M5 7l5 5 5-5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
            </button>

            {/* options menu */}
            {open && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: 4,
                        zIndex: 50,
                        backgroundColor: tokens.colors.surface,
                        border: `1px solid ${tokens.colors.border}`,
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        overflow: 'hidden',
                    }}
                >
                    {/* Search Input */}
                    <div style={{ 
                        padding: tokens.spacing.xs, 
                        borderBottom: `1px solid ${tokens.colors.borderSubtle}` 
                    }}>
                        <input
                            type="text"
                            placeholder="Search..."
                            style={{
                                width: '100%',
                                padding: `${tokens.spacing.xxs}px ${tokens.spacing.xs}px`,
                                fontSize: 11,
                                border: `1px solid ${tokens.colors.border}`,
                                borderRadius: 4,
                                backgroundColor: tokens.colors.headerBg,
                                color: tokens.colors.textPrimary,
                                outline: 'none',
                            }}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                        />
                    </div>

                    <ul style={{ 
                        maxHeight: 220, 
                        overflow: 'auto', 
                        padding: `${tokens.spacing.xxs}px 0`,
                        margin: 0,
                        listStyle: 'none',
                    }}>
                        {options
                            .filter((opt) =>
                                opt.label
                                    .toLowerCase()
                                    .includes(searchTerm.toLowerCase()),
                            )
                            .map((opt) => {
                                const isActive =
                                    String(opt.value) ===
                                    String(selectedOption?.value);
                                return (
                                    <li key={opt.value}>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleSelect(opt.value)
                                            }
                                            style={{
                                                display: 'flex',
                                                width: '100%',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
                                                fontSize: 12,
                                                fontWeight: isActive ? 500 : 400,
                                                color: isActive ? 'rgb(1, 119, 204)' : tokens.colors.textPrimary,
                                                backgroundColor: isActive ? 'rgba(1, 149, 255, 0.06)' : 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                transition: 'background-color 0.1s ease',
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isActive) {
                                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = tokens.colors.hoverRow;
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isActive) {
                                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                                                }
                                            }}
                                        >
                                            <span>{opt.label}</span>
                                            {isActive && (
                                                <span style={{ color: 'rgb(1, 149, 255)' }}>
                                                    <svg
                                                        width="14"
                                                        height="14"
                                                        viewBox="0 0 20 20"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M5 10.5l3 3 7-7"
                                                            stroke="currentColor"
                                                            strokeWidth="1.7"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        {options.filter((opt) =>
                            opt.label
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase()),
                        ).length === 0 && (
                            <li style={{
                                padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
                                textAlign: 'center',
                                fontSize: 11,
                                color: tokens.colors.textMuted,
                            }}>
                                No options found
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default FilterDropdown;
